import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { getDb } from '@data/db/database';
import { resolveActiveBusiness } from '@data/services/businessSession';
import { listTaxRates } from '@data/services/productReader';

import { formatMoney } from '@core/money/format';
import { asMoney } from '@core/money/types';
import { toast } from '@ui/components/Toaster';
import { useCreateQuotation, useQuotation, useQuotationOptions, useUpdateQuotation } from './useQuotations';
import { blankQuotation, computeQuotationTotals, defaultQuotationCoverLetter, type QuotationFormState } from './quotationDraft';
import { parseMoney, parseQuantity } from '@core/money';
import { STANDARD_UNITS } from '@features/masterData/units';
import { STANDARD_GST_RATES, formatGstRateLabel } from '@features/masterData/gstRates';
import { DocumentScreen } from '@features/documents/DocumentScreen';

let rowCounter = 0;
const lineId = () => `q-line-${++rowCounter}`;
const input = 'w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100';
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const validLine = (quantity: string, rate: string, precision: number) => {
  try { return Number(parseQuantity(quantity, precision)) > 0 && Number(parseMoney(rate)) >= 0; }
  catch { return false; }
};

const QUOTE_GRID_MOBILE = 'grid grid-cols-2 gap-2 sm:grid-cols-[minmax(280px,1fr)_75px_90px_130px_100px_130px_2%]';

export function QuotationEditorScreen({ documentId = null }: { documentId?: string | null }) {
  const options = useQuotationOptions();
  const existing = useQuotation(documentId);
  const create = useCreateQuotation();
  const update = useUpdateQuotation();
  const [business, setBusiness] = useState<Awaited<ReturnType<typeof resolveActiveBusiness>> | null>(null);
  const [state, setState] = useState<QuotationFormState>(() => blankQuotation(lineId(), today()));
  const [loaded, setLoaded] = useState<string | null>(null);
  const [taxRates, setTaxRates] = useState<Array<{ id: string; name: string; rateBp: number }>>([]);

  useEffect(() => {
    void resolveActiveBusiness(getDb() as never).then(setBusiness).catch(() => setBusiness(null));
  }, []);

  useEffect(() => {
    if (business) {
      listTaxRates({ taxRates: getDb().taxRates }, business.id).then(setTaxRates);
    }
  }, [business]);

  const products = useMemo(() => options.data?.products ?? [], [options.data?.products]);
  const customers = options.data?.customers ?? [];
  const productsById = useMemo(() => new Map(products.map((p) => [p.product.id, p])), [products]);

  if (documentId && existing.data?.stored.status === 'DRAFT' && loaded !== documentId) {
    const row = existing.data.stored;
    const lines = existing.data.model.lines.map((line) => ({
      id: lineId(),
      productId: line.productId ?? '',
      quantity: String(line.quantityMilli / 1000),
      rate: String(line.ratePaise / 100),
      description: line.description,
      unit: line.unit ?? 'NOS',
      taxRateBp: line.taxRateBp ?? 0,
      discountType: 'NONE' as const,
      discountValue: 0,
    }));
    setState({
      ...blankQuotation(lineId(), row.docDate),
      customerId: row.customerId ?? '',
      docDate: row.docDate,
      lines,
      validityDate: row.expiryDate ?? '',
      coverLetterEnabled: row.coverLetterEnabled ?? false,
      coverLetter: row.coverLetter ?? '',
      businessTerms: row.terms ?? '',
      placeOfSupplyOverride: row.placeOfSupplyCode,
    });
    setLoaded(documentId);
  }

  const customer = customers.find((c) => c.id === state.customerId) ?? null;
  const totals = business ? computeQuotationTotals({ business, customer, state, productsById }) : null;
  const editing = documentId !== null;

  if (editing && existing.data && existing.data.stored.status !== 'DRAFT') return <DocumentScreen documentId={documentId} />;

  async function save() {
    if (!business || !customer || !state.docDate || !state.lines.length || state.lines.some((l) => !l.productId || !validLine(l.quantity, l.rate, productsById.get(l.productId)?.product.unitPrecision ?? 3) || Number(l.quantity) <= 0)) {
      toast({ title: 'Complete the quotation details', tone: 'danger' });
      return;
    }
    const payload = {
      customerId: state.customerId,
      docDate: state.docDate,
      ...(state.validityDate ? { validityDate: state.validityDate } : {}),
      lines: state.lines.map((l) => ({
        productId: l.productId,
        quantityMilli: parseQuantity(l.quantity, productsById.get(l.productId)?.product.unitPrecision ?? 3),
        ratePaise: parseMoney(l.rate),
        ...(l.description.trim() ? { description: l.description.trim() } : {}),
        taxRateBp: l.taxRateBp ?? 0,
      })),
      roundOff: state.roundOff,
      coverLetterEnabled: state.coverLetterEnabled,
      coverLetter: state.coverLetter,
      businessTerms: state.businessTerms,
      placeOfSupplyOverride: state.placeOfSupplyOverride,
    };
    try {
      const result = editing && documentId
        ? await update.mutateAsync({ id: documentId, input: payload })
        : await create.mutateAsync({ ...payload, businessId: business.id });
      toast({ title: `Quotation ${result.docNumber} saved as draft`, tone: 'success' });
      if (!editing) window.location.hash = `#/sales/quotations/${result.id}/edit`;
    } catch (error) {
      toast({ title: 'Could not save quotation', description: error instanceof Error ? error.message : 'Try again.', tone: 'danger' });
    }
  }

  if (options.isLoading || (editing && existing.isLoading) || !business) return <p className="p-4 text-sm text-slate-400">Loading quotation…</p>;

  return <div className="flex flex-col gap-4 p-4 text-slate-100">
    <header className="flex items-center justify-between">
      <div>
        <h1 className="text-lg font-semibold">{editing ? 'Edit quotation draft' : 'New quotation'}</h1>
        <p className="text-xs text-slate-400">A quotation does not reserve stock or create a receivable.</p>
      </div>
      {editing && existing.data && <span data-testid="quotation-number">{existing.data.stored.docNumber}</span>}
    </header>
    {!customers.length || !products.length ? (
      <p className="text-sm text-slate-400">Add a customer and at least one product before creating a quotation.</p>
    ) : (
      <div className="grid gap-4 xl:grid-cols-[1fr_19rem]">
        <section className="space-y-3 min-w-0">
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="text-xs text-slate-400">Customer
              <select data-testid="quotation-customer" className={input} value={state.customerId} onChange={(e) => setState(s => ({ ...s, customerId: e.target.value }))}>
                <option value="">Choose customer…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="text-xs text-slate-400">Date
              <input data-testid="quotation-date" type="date" className={input} value={state.docDate} onChange={(e) => setState(s => ({ ...s, docDate: e.target.value }))} />
            </label>
            <label className="text-xs text-slate-400">Valid until
              <input data-testid="quotation-validity" type="date" className={input} value={state.validityDate} onChange={(e) => setState(s => ({ ...s, validityDate: e.target.value }))} />
            </label>
          </div>
          <div className={QUOTE_GRID_MOBILE}>
              <div className="text-xs uppercase text-slate-400 font-semibold">Product / Description</div>
              <div className="text-xs uppercase text-slate-400 font-semibold text-center">Qty</div>
              <div className="text-xs uppercase text-slate-400 font-semibold text-center">Unit</div>
              <div className="text-xs uppercase text-slate-400 font-semibold text-right">Rate</div>
              <div className="text-xs uppercase text-slate-400 font-semibold text-center">GST</div>
              <div className="text-xs uppercase text-slate-400 font-semibold text-right">Amount</div>
              <div className="text-xs uppercase text-slate-400 font-semibold">Action</div>
              {state.lines.map((line) => (
                <React.Fragment key={line.id}>
                  <div className="py-2 pr-2">
                    <select data-testid="quotation-product" className={input} value={line.productId} onChange={(e) => {
                      const p = productsById.get(e.target.value);
                      setState(s => ({
                        ...s,
                        lines: s.lines.map(l => l.id === line.id ? {
                          ...l,
                          productId: e.target.value,
                          rate: p ? String(p.product.salePricePaise / 100) : '',
                          taxRateBp: p?.taxRateBp ?? 0,
                        } : l)
                      }));
                    }}>
                      <option value="">Choose product…</option>
                      {products.map(p => <option key={p.product.id} value={p.product.id}>{p.product.name}</option>)}
                    </select>
                    <input className={`${input} mt-1`} aria-label="Quotation line description" placeholder="Description" value={line.description} onChange={(e) => setState(s => ({ ...s, lines: s.lines.map(l => l.id === line.id ? { ...l, description: e.target.value } : l) }))} />
                  </div>
                  <div className="text-center">
                    <input data-testid="quotation-quantity" className={`${input} text-center`} inputMode="decimal" value={line.quantity} onChange={(e) => setState(s => ({ ...s, lines: s.lines.map(l => l.id === line.id ? { ...l, quantity: e.target.value } : l) }))} />
                  </div>
                  <div className="text-center">
                    <select data-testid="quotation-unit" className={input} value={line.unit} onChange={(e) => setState(s => ({ ...s, lines: s.lines.map(l => l.id === line.id ? { ...l, unit: e.target.value } : l) }))}>
                      {STANDARD_UNITS.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
                    </select>
                  </div>
                  <div className="text-right">
                    <input data-testid="quotation-rate" className={`${input} text-right`} inputMode="decimal" value={line.rate} onChange={(e) => setState(s => ({ ...s, lines: s.lines.map(l => l.id === line.id ? { ...l, rate: e.target.value } : l) }))} />
                  </div>
                  <div className="text-center">
                    <select data-testid="quotation-gst" className={input} value={String(line.taxRateBp)} onChange={(e) => setState(s => ({ ...s, lines: s.lines.map(l => l.id === line.id ? { ...l, taxRateBp: Number(e.target.value) } : l) }))}>
                      <option value="0">0%</option>
                      {taxRates.length > 0
                        ? taxRates.filter(tr => tr.rateBp !== 0).map(tr => <option key={tr.id} value={String(tr.rateBp)}>{formatGstRateLabel(tr.rateBp)}</option>)
                        : STANDARD_GST_RATES.filter(tr => tr.rateBp !== 0).map(tr => <option key={String(tr.rateBp)} value={String(tr.rateBp)}>{formatGstRateLabel(tr.rateBp)}</option>)}
                    </select>
                  </div>
                  <div className="text-right font-mono text-sm">
                    {(() => {
                      const idx = state.lines.findIndex(l => l.id === line.id);
                      const tl = idx >= 0 ? totals?.lines[idx] : undefined;
                      return tl?.totalPaise ? formatMoney(asMoney(tl.totalPaise)) : '';
                    })()}
                  </div>
                  <div>
                    <button type="button" aria-label="Remove quotation line" onClick={() => setState(s => ({ ...s, lines: s.lines.filter(l => l.id !== line.id) }))}>
                      <Trash2 className="h-4 w-4 mx-auto" />
                    </button>
                  </div>
                </React.Fragment>
              ))}
            </div>
            <button type="button" className="rounded border border-slate-700 px-3 py-2 text-sm" onClick={() => {
            setState((s) => ({
              ...s,
              lines: [...s.lines, { id: lineId(), productId: '', quantity: '1', rate: '', description: '', unit: 'NOS', taxRateBp: 0, discountType: 'NONE', discountValue: 0 }],
            }));
          }}>
            <Plus className="mr-1 inline h-4 w-4" />Add line
          </button>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={state.coverLetterEnabled} onChange={(e) => setState(s => ({ ...s, coverLetterEnabled: e.target.checked, coverLetter: s.coverLetter || defaultQuotationCoverLetter(business.legalName) }))} />
              Include cover letter
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={state.roundOff} onChange={(e) => setState(s => ({ ...s, roundOff: e.target.checked }))} />
              Round off total
            </label>
          </div>
          {state.coverLetterEnabled && <label className="block text-xs text-slate-400">Cover letter<textarea data-testid="quotation-cover-letter" className={`${input} mt-1`} rows={6} value={state.coverLetter} onChange={(e) => setState(s => ({ ...s, coverLetter: e.target.value }))} /></label>}
          <label className="block text-xs text-slate-400">Business terms<textarea data-testid="quotation-business-terms" className={`${input} mt-1`} rows={4} value={state.businessTerms} onChange={(e) => setState(s => ({ ...s, businessTerms: e.target.value }))} /></label>
        </section>
        <aside className="h-fit rounded border border-slate-800 p-4">
          <h2 className="mb-3 text-xs uppercase text-slate-400">Quotation total</h2>
          <div className="flex justify-between border-t border-slate-700 py-2 font-semibold">
            <span>Total</span>
            <span data-testid="quotation-total">{totals ? formatMoney(asMoney(totals.grandTotalPaise)) : '—'}</span>
          </div>
          <button type="button" data-testid="quotation-save" disabled={!totals || create.isPending || update.isPending} onClick={() => void save()} className="mt-3 w-full rounded bg-brand-500 px-3 py-2 text-sm font-medium disabled:opacity-40">
            {editing ? 'Save changes' : 'Save draft'}
          </button>
          {editing && documentId && <a className="mt-2 block rounded border border-slate-700 px-3 py-2 text-center text-sm" href={`#/sales/quotations/${documentId}`}>Preview without saving</a>}
          <a className="mt-2 block text-center text-sm text-slate-400" href="#/sales/quotations">Back to quotations</a>
        </aside>
      </div>
    )}
  </div>;
}